// ============================================================================
//  MembershipSettings — how fast the renewal campaign is allowed to run
// ============================================================================
//
//  ── WHAT THIS IS FOR ───────────────────────────────────────────────────────
//
//  Every "Mark renewed" on the register is a call to NHIA's eligibility
//  service. That service is not ours and it has its own limits. A campaign that
//  spends its allowance by mid-morning gets refusals for the rest of the day
//  that look, on screen, exactly like dead memberships — so the day's work is
//  wasted AND the register fills up with wrong answers that somebody has to
//  unpick later. The ceiling belongs somewhere the facility can set it.
//
//  ── THIS SCREEN DOES NOT ENFORCE ANYTHING ──────────────────────────────────
//
//  Worth being blunt about, because it looks like it does. These numbers are
//  stored, and the CHECK ENDPOINT reads them before it calls out. Nothing here
//  blocks anything. That is the only arrangement that works: two staff with the
//  register open are two browsers each counting only their own clicks, and the
//  server is the one place that sees both.
//
//  The bars below are therefore a report of what the server has already spent,
//  not a tally this page is keeping.
//
//  ── WHAT COUNTS AGAINST A LIMIT ────────────────────────────────────────────
//
//  A check, not a successful renewal. A card that comes back INACTIVE cost NHIA
//  exactly as much as one that came back with a CCC. Said on the screen too,
//  because "27 of 48" is otherwise read as "27 renewed".
//
//  ── ZERO MEANS NO LIMIT ────────────────────────────────────────────────────
//
//  Not "nothing allowed". It is the difference between an uncapped campaign and
//  a stopped one, so the field says so in words rather than leaving it to be
//  discovered.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, TextField, Button, Alert, Switch, Divider,
  Snackbar, CircularProgress, Tooltip, InputAdornment,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import SpeedIcon from '@mui/icons-material/Speed';
import FlagIcon from '@mui/icons-material/Flag';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { membershipSettings } from '../BaseURL';

const PRIMARY = '#1A9B7F';
const PRIMARY_DARK = '#137A64';
const INK = '#0F172A';
const MUTED = '#64748B';
const FAINT = '#94A3B8';
const LINE = '#E7EBF0';
const SOFT = '#F6F8FA';
const CARD = {
  borderRadius: 3, border: `1px solid ${LINE}`, background: '#fff',
  boxShadow: '0 1px 2px rgba(15,23,42,.04)',
};

const currentUser = () => {
  try { return JSON.parse(localStorage.getItem('userInfo') || '{}')?.name || ''; }
  catch { return ''; }
};

/*  The limits, in the order they bite. Day first: it is the one that ends an
 *  afternoon, and the one the facility actually decided on.                  */
const LIMITS = [
  {
    key: 'max_per_day', window: 'day', label: 'Renewals per day',
    hint: 'The whole day\'s allowance. Resets at midnight.',
    max: 100000,
  },
  {
    key: 'max_per_hour', window: 'hour', label: 'Per hour',
    hint: 'Stops a day\'s allowance going in one burst.',
    max: 10000,
  },
  {
    key: 'max_per_minute', window: 'minute', label: 'Per minute',
    hint: 'Paces somebody clicking through a claimed batch.',
    max: 1000,
  },
];

/*  ── WHY THESE TWO LIVE OUT HERE AND NOT INSIDE THE COMPONENT ───────────────
 *
 *  THE BUG THIS FIXES, and it made the fields close to unusable: `LimitRow` was
 *  declared inside MembershipSettings. A component declared in a render body is
 *  a NEW FUNCTION on every render, and React compares component types by
 *  identity — so every keystroke produced a "different" component, the old
 *  subtree was unmounted, and the <input> inside it was destroyed and rebuilt.
 *  The DOM node that had focus no longer existed. You got one digit per click:
 *  type a character, lose the caret, reach for the mouse, click, type the next.
 *
 *  Declared at module scope the function identity is stable, React reconciles
 *  the same input element across renders, and the caret stays where it was.
 *
 *  ── AND WHY THE FIELD HOLDS TEXT, NOT A NUMBER ─────────────────────────────
 *
 *  It was `type="number"` with `Number(e.target.value)` on every change, which
 *  breaks three ordinary things even once focus is fixed:
 *
 *    · clearing the box to retype snapped it to 0, so "600" over "48" meant
 *      fighting a field that kept refilling itself
 *    · a half-typed value was coerced mid-keystroke
 *    · the mouse wheel over a focused number input silently changes it, which
 *      on a page of ceilings is a genuinely bad way to lose a setting
 *
 *  So the draft is kept as a STRING while the box is focused, digits only, and
 *  is turned into a number when focus leaves. The parent holds numbers at all
 *  times, so saving is unchanged.                                             */
const NumberField = ({ value, onCommit, disabled, max, endText, width = 150 }) => {
  const [draft, setDraft] = React.useState(null);   // null = not being edited
  const shown = draft ?? String(value ?? 0);

  const commit = () => {
    if (draft === null) return;
    const n = draft === '' ? 0 : Number(draft);
    setDraft(null);
    onCommit(Number.isFinite(n) ? Math.min(Math.max(n, 0), max) : 0);
  };

  return (
    <TextField
      size="small"
      value={shown}
      disabled={disabled}
      //  Digits only, stripped as typed — so a pasted "1,200" or a stray letter
      //  cannot reach the parent as NaN.
      onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ''))}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { commit(); e.target.blur(); } }}
      //  `text` + inputMode, NOT type="number": keeps the numeric keypad on a
      //  phone without the scroll-wheel behaviour or the spinner arrows.
      inputProps={{
        inputMode: 'numeric', pattern: '[0-9]*', maxLength: 7,
        style: { textAlign: 'right', fontWeight: 700 },
      }}
      InputProps={{
        endAdornment: endText ? (
          <InputAdornment position="end">
            <Typography sx={{ fontSize: 11, color: FAINT }}>{endText}</Typography>
          </InputAdornment>
        ) : undefined,
      }}
      sx={{ width, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
    />
  );
};

//  One limit: the field, and what the SERVER has spent against it. Everything
//  it needs arrives as a prop — it reads no state of its own beyond the draft
//  inside NumberField.
const LimitRow = ({ l, cap, spent, attempts, enabled, onCommit }) => {
  const uncapped = cap === 0;
  const pct = uncapped ? 0 : Math.min(100, (spent / cap) * 100);
  const over = !uncapped && spent >= cap;
  const near = !uncapped && !over && pct >= 75;
  const barColor = over ? '#B91C1C' : near ? '#B45309' : PRIMARY;

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }}>
            {l.label}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: MUTED, mt: .25 }}>
            {l.hint}
          </Typography>
        </Box>
        <NumberField
          value={cap} disabled={!enabled} max={l.max}
          endText={uncapped ? 'no limit' : `/ ${l.window}`}
          onCommit={onCommit}
        />
      </Box>

      {/*  What the SERVER has spent. Not a tally this page keeps. */}
      {enabled && (
        <Box sx={{ mt: 1.25, opacity: uncapped ? .45 : 1 }}>
          <Box sx={{
            height: 6, borderRadius: 99, background: '#F1F5F9', overflow: 'hidden',
          }}>
            <Box sx={{
              width: `${pct}%`, height: '100%', background: barColor,
              transition: 'width .3s ease, background .3s ease',
            }} />
          </Box>
          <Typography sx={{ fontSize: 11, color: over ? '#B91C1C' : FAINT, mt: .5 }}>
            {uncapped
              ? `${spent.toLocaleString()} renewed ${l.window === 'day' ? 'today' : `this ${l.window}`} — no ceiling set`
              : over
                ? `Full — ${spent} of ${cap} renewed. Nothing more until this ${l.window} rolls over.`
                : `${spent} of ${cap} renewed · ${cap - spent} left`}
            {attempts > spent && (
              <Box component="span" sx={{ color: FAINT }}>
                {' · '}{attempts.toLocaleString()} tried, {attempts - spent} produced no CCC
              </Box>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default function MembershipSettings() {
  const me = useMemo(currentUser, []);

  const [loaded, setLoaded] = useState(null);   // what the server holds
  const [form, setForm] = useState(null);       // what is being edited
  const [usage, setUsage] = useState(null);
  const [stored, setStored] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axios.get(membershipSettings);
      setLoaded(data.settings);
      setForm(data.settings);
      setUsage(data.usage);
      setStored(data.stored !== false);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  //  Only what actually changed is sent, so a save cannot quietly rewrite a
  //  setting somebody else changed while this page was open.
  const changed = useMemo(() => {
    if (!loaded || !form) return {};
    const out = {};
    for (const k of Object.keys(form)) {
      if (String(form[k]) !== String(loaded[k])) out[k] = form[k];
    }
    return out;
  }, [loaded, form]);
  const dirty = Object.keys(changed).length > 0;

  const save = useCallback(async () => {
    setSaving(true); setError(null);
    try {
      const { data } = await axios.put(membershipSettings, { settings: changed, user: me });
      setLoaded(data.settings);
      setForm(data.settings);
      setUsage(data.usage);
      setToast('Saved. The register applies this from the next renewal.');
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setSaving(false); }
  }, [changed, me]);

  if (loading && !form) {
    return (
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 4 }}>
        <CircularProgress size={18} sx={{ color: PRIMARY }} />
        <Typography sx={{ fontSize: 13, color: MUTED }}>Reading settings…</Typography>
      </Box>
    );
  }
  if (!form) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error || 'Settings could not be read.'}
        </Alert>
      </Box>
    );
  }

  const enabled = !!form.limits_enabled;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 780, mx: 'auto' }}>
      {/* ── header ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box sx={{
          width: 42, height: 42, borderRadius: 2.5, display: 'grid', placeItems: 'center',
          background: `${PRIMARY}14`, color: PRIMARY,
        }}><TuneIcon /></Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 800, color: INK, lineHeight: 1.2 }}>
            Settings
          </Typography>
          <Typography sx={{ fontSize: 12, color: MUTED }}>
            How fast the renewal campaign is allowed to run
          </Typography>
        </Box>
        <Tooltip title="Re-read from the server">
          <span>
            <Button size="small" onClick={load} disabled={loading}
              startIcon={<RefreshIcon sx={{ fontSize: 17 }} />}
              sx={{ textTransform: 'none', fontWeight: 600, color: MUTED }}>
              Refresh
            </Button>
          </span>
        </Tooltip>
      </Box>

      {!stored && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          Migration 073 has not been run on this database, so these are the
          built-in defaults and cannot be saved. They are still being applied.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── the limits ──────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ ...CARD, mb: 2, overflow: 'hidden' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 1.75,
          borderBottom: `1px solid ${LINE}`, background: SOFT,
        }}>
          <SpeedIcon sx={{ fontSize: 19, color: enabled ? PRIMARY : FAINT }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: INK }}>
              Renewal limits
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: MUTED }}>
              {enabled ? 'Being applied to every renewal' : 'Switched off — nothing is capped'}
            </Typography>
          </Box>
          <Switch checked={enabled} onChange={(e) => set('limits_enabled', e.target.checked ? 1 : 0)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY },
            }} />
        </Box>

        <Box sx={{ px: 2.25 }}>
          {LIMITS.map((l, i) => (
            <React.Fragment key={l.key}>
              {i > 0 && <Divider sx={{ borderColor: LINE }} />}
              <LimitRow
                l={l}
                cap={Number(form[l.key] || 0)}
                spent={Number(usage?.used?.[l.window] || 0)}
                //  Attempts, shown beside the count that is actually enforced.
                //  The gap between them is the whole reason this screen exists.
                attempts={Number(usage?.tried?.[l.window] || 0)}
                enabled={enabled}
                onCommit={(n) => set(l.key, n)}
              />
            </React.Fragment>
          ))}
        </Box>

        {/*  Said here because "27 of 48" is otherwise read as "27 renewed". */}
        <Box sx={{
          display: 'flex', gap: 1.25, px: 2.25, py: 1.5,
          borderTop: `1px solid ${LINE}`, background: SOFT,
        }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: FAINT, mt: .1, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11.5, color: MUTED, lineHeight: 1.6 }}>
            What is counted is a <strong>renewal that went through</strong> — a
            CCC was issued. A card NHIA refuses, or one that comes back with no
            CCC, renewed nobody and is <strong>not</strong> counted, so a bad
            batch does not eat the day's allowance. A renewal that is not live
            yet counts later, on the day its CCC is finally issued. Recording
            numbers at the desk is never limited; it talks to nobody.{' '}
            <strong>Zero means no limit</strong>, not none allowed.
          </Typography>
        </Box>
      </Paper>

      {/* ── targets, which are not limits ───────────────────────────────── */}
      <Paper elevation={0} sx={{ ...CARD, mb: 2, overflow: 'hidden' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 1.75,
          borderBottom: `1px solid ${LINE}`, background: SOFT,
        }}>
          <FlagIcon sx={{ fontSize: 19, color: MUTED }} />
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: INK }}>
              Targets
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: MUTED }}>
              Shown on the register. Nothing is blocked by these.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ px: 2.25 }}>
          <Box sx={{ py: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }}>
                Daily target
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: MUTED, mt: .25 }}>
                What a good day looks like. A target above the daily limit cannot
                be met, so keep it at or below it.
              </Typography>
            </Box>
            <NumberField value={form.daily_target ?? 0} max={100000}
              onCommit={(n) => set('daily_target', n)} />
          </Box>
          <Divider sx={{ borderColor: LINE }} />
          <Box sx={{ py: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }}>
                Attempts before writing off
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: MUTED, mt: .25 }}>
                How many times a member is phoned before the number is given up
                on. Zero means no guidance.
              </Typography>
            </Box>
            <NumberField value={form.max_attempts ?? 0} max={50}
              onCommit={(n) => set('max_attempts', n)} />
          </Box>
        </Box>
      </Paper>

      {form.daily_target > 0 && form.max_per_day > 0
        && Number(form.daily_target) > Number(form.max_per_day) && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          The daily target ({form.daily_target}) is above the daily limit
          ({form.max_per_day}), so it can never be reached.
        </Alert>
      )}

      {/* ── save ────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 12, color: FAINT, flex: 1, minWidth: 160 }}>
          {dirty
            ? `${Object.keys(changed).length} change${Object.keys(changed).length === 1 ? '' : 's'} not saved`
            : 'Everything saved'}
        </Typography>
        {dirty && (
          <Button onClick={() => setForm(loaded)}
            sx={{ textTransform: 'none', fontWeight: 600, color: MUTED }}>
            Discard
          </Button>
        )}
        <Button variant="contained" disableElevation startIcon={<SaveIcon />}
          onClick={save} disabled={!dirty || saving || !stored}
          sx={{
            background: PRIMARY, fontWeight: 700, borderRadius: 2,
            textTransform: 'none', px: 2.5, py: 1,
            '&:hover': { background: PRIMARY_DARK },
          }}>
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
      </Box>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setToast(null)}
          sx={{ borderRadius: 2, fontWeight: 600 }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
